/** The signed-in frame: sidebar + top bar + page outlet, idle guard, update banner, / to search. */
import { LifeBuoy, RefreshCw } from 'lucide-react';
import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { DateText, SEARCH_INPUT_ID, Spinner, cx } from '../../components/ui';
import { BRAND } from '../../config/brand';
import { useAuth } from '../../lib/auth';
import { applyUpdate, useUpdateReady } from '../../lib/pwa';
import { ShellContext } from './context';
import { IdleGuard } from './IdleGuard';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

const COLLAPSE_KEY = 'dms.nav.collapsed';

const typing = (el: EventTarget | null) => el instanceof HTMLElement && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

export function AppShell() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const updateReady = useUpdateReady();
  const navigate = useNavigate();
  const { user } = useAuth();

  // "/" focuses the search box from anywhere (NFR-08 keyboard), unless you are typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || typing(e.target)) return;
      const box = document.getElementById(SEARCH_INPUT_ID);
      e.preventDefault();
      if (box && box.offsetParent !== null) (box as HTMLInputElement).focus();
      else navigate('/search');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate]);

  useEffect(() => setMobileOpen(false), [pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMobileOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const toggleCollapse = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1');
      } catch {
        /* ignore */
      }
      return !c;
    });
  };

  return (
    <div className="min-h-dvh">
      <a href="#main" className="sr-only z-50 rounded bg-card px-3 py-2 focus:not-sr-only focus:fixed focus:left-2 focus:top-2">
        Skip to content
      </a>
      <aside className={cx('fixed inset-y-0 left-0 z-40 hidden lg:block', collapsed ? 'w-[72px]' : 'w-64')}>
        <Sidebar collapsed={collapsed} onToggleCollapse={toggleCollapse} />
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <div aria-hidden className="absolute inset-0 bg-[rgb(var(--shadow)/0.5)]" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[min(300px,85vw)] shadow-lift">
            <Sidebar collapsed={false} onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}
      <div className={cx('flex min-h-dvh flex-col', collapsed ? 'lg:pl-[72px]' : 'lg:pl-64')}>
        {updateReady && (
          <button type="button" onClick={() => void applyUpdate()} className="flex w-full items-center justify-center gap-2 bg-info-soft px-4 py-2 text-sm font-semibold text-info">
            <RefreshCw aria-hidden className="h-4 w-4" />A new version of {BRAND.product} is ready. Tap to update.
          </button>
        )}
        {user?.platformAdmin && user.supportSessionUntil && (
          <p role="status" className="flex items-center justify-center gap-2 bg-warn-soft px-4 py-2 text-sm font-semibold text-warn">
            <LifeBuoy aria-hidden className="h-4 w-4" />
            Support session in {user.org?.name}, until <DateText value={user.supportSessionUntil} mode="when" />. Everything you open is logged.
          </p>
        )}
        <TopBar onMenu={() => setMobileOpen(true)} />
        <main id="main" className="flex-1">
          <ShellContext.Provider value={{ collapsed }}>
            <Suspense fallback={<Spinner />}>
              <Outlet />
            </Suspense>
          </ShellContext.Provider>
        </main>
      </div>
      <IdleGuard />
    </div>
  );
}
